'use client'

import * as React from 'react'
import { Search, X, Check, Loader2, Users, ChevronDown, ChevronUp, AlertTriangle, Filter, Grid, List as ListIcon, Eye } from 'lucide-react'
import { 
  borderRadiusV3, 
  spacingV3, 
  shadowsV3, 
  animationsV3, 
  glassV3,
  componentSpacing,
  componentAnimations,
  componentShadows,
  componentGlass,
  typographyV3,
  componentTypography
} from '@birhaus/tokens/v3'
import { cn, ClassValue } from '@birhaus/types/utils'

/**
 * BIRHAUS List V3 - Radical Minimalist Searchable List
 * 
 * Revolutionary list component with:
 * - Glass morphism with backdrop blur
 * - Smooth item animations and hover effects
 * - Radical border radius (20px default)
 * - Generous spacing (2x traditional)
 * - Miller's Law compliance (max 7±2 items)
 * - Enhanced search with debouncing
 * - Spanish-first labeling
 * - Performance optimized virtual scrolling
 */

// Using imported cn utility from @birhaus/types/utils

// Create a debounced search hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState(value)
  
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)
    
    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])
  
  return debouncedValue
}

export interface BirhausListV3Item {
  id: string
  title: string
  titleEs?: string
  titleEn?: string
  subtitle?: string
  subtitleEs?: string
  subtitleEn?: string
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  avatar?: React.ReactNode
  badge?: string | number
  badgeColor?: 'blue' | 'red' | 'green' | 'yellow' | 'purple'
  metadata?: Record<string, any>
  disabled?: boolean
  selectable?: boolean
  
  // V3 enhancements
  glow?: boolean
  glass?: boolean
  priority?: 'high' | 'medium' | 'low'
}

export interface BirhausListV3Props {
  // Core data
  items: BirhausListV3Item[]
  
  // V3 Design variants
  variant?: 'default' | 'glass' | 'frosted' | 'minimal' | 'cards'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  radius?: keyof typeof borderRadiusV3
  layout?: 'list' | 'grid' | 'compact'
  
  // V3 Visual effects
  glassMorphism?: boolean
  glow?: boolean
  float?: boolean
  hoverEffects?: boolean
  
  // Search functionality
  searchable?: boolean
  searchKeys?: string[]
  searchPlaceholder?: string
  searchPlaceholderEs?: string
  searchPlaceholderEn?: string
  debounceMs?: number
  
  // Selection
  selectable?: boolean
  multiSelect?: boolean
  selectedItems?: Set<string>
  onSelect?: (item: BirhausListV3Item) => void
  onMultiSelect?: (items: Set<string>) => void
  maxSelectable?: number
  
  // Filtering and sorting
  filterable?: boolean
  filters?: Record<string, any>
  onFilter?: (filters: Record<string, any>) => void
  sortBy?: (a: BirhausListV3Item, b: BirhausListV3Item) => number
  groupBy?: (item: BirhausListV3Item) => string
  
  // Miller's Law compliance
  maxVisibleItems?: number
  showCognitiveWarning?: boolean
  cognitiveMode?: 'strict' | 'warning' | 'disabled'
  
  // Display options
  height?: number | string
  virtualized?: boolean
  itemHeight?: number
  emptyState?: React.ReactNode
  loading?: boolean
  
  // Labels (Spanish-first)
  emptyMessage?: string
  emptyMessageEs?: string
  emptyMessageEn?: string
  emptySearchMessage?: string
  emptySearchMessageEs?: string
  emptySearchMessageEn?: string
  selectAllLabel?: string
  selectAllLabelEs?: string
  selectAllLabelEn?: string
  selectedCountLabel?: string
  
  // Item customization
  renderItem?: (item: BirhausListV3Item, index: number, isSelected: boolean) => React.ReactNode
  onItemClick?: (item: BirhausListV3Item, index: number) => void
  onItemDoubleClick?: (item: BirhausListV3Item, index: number) => void
  onItemHover?: (item: BirhausListV3Item, index: number) => void
  
  // Performance
  trackPerformance?: boolean
  
  // Styling
  className?: string
  listClassName?: string
  itemClassName?: string | ((item: BirhausListV3Item, index: number) => string)
}

export const BirhausListV3: React.FC<BirhausListV3Props> = ({
  items,
  
  // V3 Design
  variant = 'glass',
  size = 'md',
  radius = 'lg',
  layout = 'list',
  
  // V3 Effects
  glassMorphism = true,
  glow = false,
  float = true,
  hoverEffects = true,
  
  // Search
  searchable = true,
  searchKeys = ['title', 'subtitle', 'description'],
  searchPlaceholder,
  searchPlaceholderEs,
  searchPlaceholderEn,
  debounceMs = 300,
  
  // Selection
  selectable = false,
  multiSelect = false,
  selectedItems = new Set(),
  onSelect,
  onMultiSelect,
  maxSelectable,
  
  // Filtering and sorting
  filterable = false,
  filters,
  onFilter,
  sortBy,
  groupBy,
  
  // Miller's Law
  maxVisibleItems = 7,
  showCognitiveWarning = true,
  cognitiveMode = 'warning',
  
  // Display
  height = '400px',
  virtualized = false,
  itemHeight = 72,
  emptyState,
  loading = false,
  
  // Labels
  emptyMessage,
  emptyMessageEs,
  emptyMessageEn,
  emptySearchMessage,
  emptySearchMessageEs,
  emptySearchMessageEn,
  selectAllLabel = 'Seleccionar todos',
  selectAllLabelEs,
  selectAllLabelEn,
  selectedCountLabel,
  
  // Customization
  renderItem,
  onItemClick,
  onItemDoubleClick,
  onItemHover,
  
  // Performance
  trackPerformance = false,
  
  // Styling
  className,
  listClassName,
  itemClassName,
}) => {
  const [searchTerm, setSearchTerm] = React.useState('')
  const [hoveredItem, setHoveredItem] = React.useState<string>()
  const [isGridView, setIsGridView] = React.useState(layout === 'grid')
  
  const debouncedSearchTerm = useDebounce(searchTerm, debounceMs)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || english || fallback || ''
  }
  
  const displaySearchPlaceholder = resolveText(
    searchPlaceholderEs,
    searchPlaceholderEn,
    searchPlaceholder || 'Buscar elementos...'
  )
  
  const displayEmptyMessage = resolveText(
    emptyMessageEs,
    emptyMessageEn,
    emptyMessage || 'No hay elementos disponibles'
  )
  
  const displayEmptySearchMessage = resolveText(
    emptySearchMessageEs,
    emptySearchMessageEn,
    emptySearchMessage || 'No se encontraron elementos que coincidan con tu búsqueda'
  )
  
  const displaySelectAllLabel = resolveText(
    selectAllLabelEs,
    selectAllLabelEn,
    selectAllLabel
  )
  
  // Filter and search items
  const filteredItems = React.useMemo(() => {
    let result = [...items]
    
    // Apply search filter
    if (debouncedSearchTerm.trim() && searchKeys.length > 0) {
      result = result.filter(item => {
        const searchText = debouncedSearchTerm.toLowerCase()
        return searchKeys.some(key => {
          const value = item[key as keyof BirhausListV3Item]
          if (typeof value === 'string') {
            return value.toLowerCase().includes(searchText)
          }
          return false
        })
      })
    }
    
    // Apply custom filters
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          result = result.filter(item => {
            const itemValue = item.metadata?.[key] || item[key as keyof BirhausListV3Item]
            return itemValue === value
          })
        }
      })
    }
    
    // Apply sorting
    if (sortBy) {
      result.sort(sortBy)
    }
    
    return result
  }, [items, debouncedSearchTerm, searchKeys, filters, sortBy])
  
  // Group items if groupBy is provided
  const groupedItems = React.useMemo(() => {
    if (!groupBy) return { 'all': filteredItems }
    
    const groups: Record<string, BirhausListV3Item[]> = {}
    filteredItems.forEach(item => {
      const group = groupBy(item)
      if (!groups[group]) groups[group] = []
      groups[group].push(item)
    })
    
    return groups
  }, [filteredItems, groupBy])
  
  // Miller's Law compliance warning
  React.useEffect(() => {
    if (cognitiveMode === 'disabled') return
    
    const itemCount = filteredItems.length
    if (showCognitiveWarning && itemCount > maxVisibleItems) {
      const message = `[BIRHAUS List V3] Cognitive overload: ${itemCount} items visible (recommended max: ${maxVisibleItems}). Consider pagination or grouping.`
      
      if (cognitiveMode === 'strict') {
        throw new Error(message)
      } else {
        console.warn(message)
      }
    }
  }, [filteredItems.length, maxVisibleItems, showCognitiveWarning, cognitiveMode])
  
  // V3 Size classes with generous spacing
  const sizeClasses = {
    sm: 'p-4 gap-2',
    md: 'p-6 gap-3',
    lg: 'p-8 gap-4',
    xl: 'p-10 gap-6',
  }
  
  // V3 Variant classes
  const variantClasses = {
    default: 'bg-white border border-gray-200',
    glass: `${componentGlass.list} backdrop-blur-lg border border-white/30`,
    frosted: `${glassV3.effects.heavy} backdrop-blur-xl border border-white/40`,
    minimal: 'bg-transparent',
    cards: 'bg-gray-50/50',
  }
  
  // Handle item selection
  const handleItemSelect = (item: BirhausListV3Item) => {
    if (!selectable || item.disabled) return
    
    if (multiSelect && onMultiSelect) {
      const newSelection = new Set(selectedItems)
      
      if (newSelection.has(item.id)) {
        newSelection.delete(item.id)
      } else {
        // Check max selectable limit
        if (maxSelectable && newSelection.size >= maxSelectable) {
          return
        }
        newSelection.add(item.id)
      }
      
      onMultiSelect(newSelection)
    } else if (onSelect) {
      onSelect(item)
    }
  }
  
  // Handle select all
  const handleSelectAll = () => {
    if (!multiSelect || !onMultiSelect) return
    
    const selectableItems = filteredItems.filter(item => !item.disabled)
    const itemsToSelect = maxSelectable 
      ? selectableItems.slice(0, maxSelectable)
      : selectableItems
    
    const isAllSelected = itemsToSelect.every(item => selectedItems.has(item.id))
    
    if (isAllSelected) {
      onMultiSelect(new Set())
    } else {
      onMultiSelect(new Set(itemsToSelect.map(item => item.id)))
    }
  }
  
  // Badge colors
  const badgeColors = {
    blue: 'bg-blue-500 text-white',
    red: 'bg-red-500 text-white',
    green: 'bg-green-500 text-white',
    yellow: 'bg-yellow-500 text-white',
    purple: 'bg-purple-500 text-white',
  }
  
  // Render individual item
  const renderListItem = (item: BirhausListV3Item, index: number) => {
    const isSelected = selectedItems.has(item.id)
    const isHovered = hoveredItem === item.id
    const displayTitle = resolveText(item.titleEs, item.titleEn, item.title)
    const displaySubtitle = resolveText(item.subtitleEs, item.subtitleEn, item.subtitle)
    const displayDescription = resolveText(item.descriptionEs, item.descriptionEn, item.description)
    
    const customItemClass = typeof itemClassName === 'function' 
      ? itemClassName(item, index) 
      : itemClassName
    
    if (renderItem) {
      return renderItem(item, index, isSelected)
    }
    
    return (
      <div
        key={item.id}
        className={cn(
          // Base styles
          'relative flex items-center gap-4',
          'p-6 transition-all duration-200',
          `rounded-[${borderRadiusV3[radius]}]`,
          'cursor-pointer select-none',
          
          // Layout variants
          isGridView && 'flex-col text-center min-h-[180px] justify-center',
          
          // Hover effects
          hoverEffects && [
            'hover:shadow-lg',
            glassMorphism && 'hover:bg-white/50',
            !glassMorphism && 'hover:bg-gray-50',
            float && 'hover:scale-[1.02] transform-gpu',
          ],
          
          // Selected state
          isSelected && [
            'ring-2 ring-blue-500/30',
            'bg-blue-50/80',
            glassMorphism && 'bg-blue-100/20',
            'shadow-md',
          ],
          
          // Glass morphism
          item.glass && 'bg-white/30 backdrop-blur-md saturate-[150%] border border-white/20 shadow-lg',
          
          // Glow effects
          item.glow && 'shadow-[0_0_20px_-6px_rgba(59,130,246,0.3)]',
          glow && isSelected && 'shadow-[0_0_20px_-6px_rgba(59,130,246,0.4)]',
          
          // Disabled state
          item.disabled && 'opacity-50 cursor-not-allowed',
          
          // Priority styling
          item.priority === 'high' && 'border-l-4 border-red-500',
          item.priority === 'medium' && 'border-l-4 border-yellow-500',
          
          customItemClass
        )}
        onClick={() => {
          if (selectable) {
            handleItemSelect(item)
          }
          onItemClick?.(item, index)
        }}
        onDoubleClick={() => onItemDoubleClick?.(item, index)}
        onMouseEnter={() => {
          setHoveredItem(item.id)
          onItemHover?.(item, index)
        }}
        onMouseLeave={() => setHoveredItem(undefined)}
      >
        {/* Selection checkbox */}
        {selectable && multiSelect && (
          <div className="flex-shrink-0">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => e.stopPropagation()}
              className={cn(
                'w-5 h-5 rounded',
                'text-blue-600 focus:ring-blue-500/20',
                'border-2 border-gray-300',
                'transition-colors duration-200'
              )}
            />
          </div>
        )}
        
        {/* Avatar */}
        {item.avatar && (
          <div className={cn(
            'flex-shrink-0',
            isGridView && 'mb-3'
          )}>
            {item.avatar}
          </div>
        )}
        
        {/* Content */}
        <div className={cn(
          'flex-1 min-w-0',
          isGridView && 'text-center'
        )}>
          {/* Title */}
          <h3 className={cn(
            'text-base font-semibold text-gray-900 mb-1',
            isGridView && 'text-center'
          )}>
            {displayTitle}
          </h3>
          
          {/* Subtitle */}
          {displaySubtitle && (
            <p className={cn(
              'text-sm text-gray-600 mb-2',
              isGridView && 'text-center'
            )}>
              {displaySubtitle}
            </p>
          )}
          
          {/* Description */}
          {displayDescription && (
            <p className={cn(
              'text-sm text-gray-500 line-clamp-2',
              isGridView && 'text-center'
            )}>
              {displayDescription}
            </p>
          )}
        </div>
        
        {/* Badge */}
        {item.badge && (
          <div className="flex-shrink-0">
            <span className={cn(
              'inline-flex items-center justify-center',
              'min-w-[24px] h-6 px-2 text-xs font-bold rounded-full',
              badgeColors[item.badgeColor || 'blue']
            )}>
              {item.badge}
            </span>
          </div>
        )}
        
        {/* Selected indicator */}
        {isSelected && !multiSelect && (
          <div className="absolute top-3 right-3">
            <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
              <Check size={14} className="text-white" />
            </div>
          </div>
        )}
      </div>
    )
  }
  
  if (loading) {
    return (
      <div className={cn(
        'flex items-center justify-center p-12',
        `rounded-[${borderRadiusV3[radius]}]`,
        variantClasses[variant],
        className
      )}>
        <div className="flex items-center gap-3">
          <Loader2 size={24} className="animate-spin text-blue-600" />
          <span className="text-gray-600 font-medium">Cargando elementos...</span>
        </div>
      </div>
    )
  }
  
  const hasItems = Object.values(groupedItems).some(group => group.length > 0)
  
  if (!hasItems && !searchTerm) {
    return (
      <div className={cn(
        'p-12 text-center',
        `rounded-[${borderRadiusV3[radius]}]`,
        variantClasses[variant],
        className
      )}>
        {emptyState || (
          <div>
            <Users size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 font-medium text-lg">{displayEmptyMessage}</p>
          </div>
        )}
      </div>
    )
  }
  
  if (!hasItems && searchTerm) {
    return (
      <div className={cn(
        'p-12 text-center',
        `rounded-[${borderRadiusV3[radius]}]`,
        variantClasses[variant],
        className
      )}>
        <Search size={48} className="mx-auto text-gray-400 mb-4" />
        <p className="text-gray-600 font-medium text-lg">{displayEmptySearchMessage}</p>
        <p className="text-gray-500 text-sm mt-2">Término de búsqueda: "{searchTerm}"</p>
      </div>
    )
  }
  
  return (
    <div className={cn(
      'flex flex-col',
      `rounded-[${borderRadiusV3[radius]}]`,
      variantClasses[variant],
      sizeClasses[size],
      glow && 'shadow-lg hover:shadow-xl',
      className
    )}>
      {/* Header with search and controls */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {/* Search */}
          {searchable && (
            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={displaySearchPlaceholder}
                className={cn(
                  'pl-10 pr-4 py-3 rounded-xl',
                  'border border-gray-200/50',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500/20',
                  'transition-all duration-200',
                  'min-w-[300px]',
                  glassMorphism && [
                    'bg-white/30 backdrop-blur-sm',
                    'placeholder:text-gray-500',
                  ],
                  !glassMorphism && 'bg-white',
                )}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          )}
          
          {/* Select all */}
          {selectable && multiSelect && (
            <button
              onClick={handleSelectAll}
              className={cn(
                'flex items-center gap-2 px-4 py-2',
                'text-sm font-medium text-blue-600',
                'hover:bg-blue-50 rounded-xl',
                'transition-colors duration-200'
              )}
            >
              <Users size={16} />
              <span>{displaySelectAllLabel}</span>
            </button>
          )}
          
          {/* Selected count */}
          {selectable && selectedItems.size > 0 && (
            <div className={cn(
              'px-4 py-2 rounded-xl',
              'bg-blue-50 text-blue-700',
              'text-sm font-medium'
            )}>
              {selectedCountLabel || `${selectedItems.size} seleccionados`}
            </div>
          )}
        </div>
        
        {/* View toggle */}
        {layout !== 'compact' && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsGridView(false)}
              className={cn(
                'p-2 rounded-lg transition-colors duration-200',
                !isGridView ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'
              )}
            >
              <ListIcon size={20} />
            </button>
            <button
              onClick={() => setIsGridView(true)}
              className={cn(
                'p-2 rounded-lg transition-colors duration-200',
                isGridView ? 'bg-blue-100 text-blue-600' : 'text-gray-400 hover:text-gray-600'
              )}
            >
              <Grid size={20} />
            </button>
          </div>
        )}
      </div>
      
      {/* Cognitive load warning */}
      {showCognitiveWarning && filteredItems.length > maxVisibleItems && (
        <div className={cn(
          'flex items-center gap-3 mb-6 p-4 rounded-2xl',
          'bg-yellow-50/80 border border-yellow-200/50',
          glassMorphism && 'backdrop-blur-sm'
        )}>
          <AlertTriangle size={20} className="text-yellow-600" />
          <div className="flex-1">
            <p className="text-yellow-900 font-medium text-sm">
              Advertencia de carga cognitiva
            </p>
            <p className="text-yellow-700 text-sm">
              Mostrando {filteredItems.length} elementos. Considera filtrar o paginar para mejorar la usabilidad.
            </p>
          </div>
        </div>
      )}
      
      {/* List content */}
      <div 
        className={cn(
          'overflow-y-auto',
          listClassName
        )}
        style={{ height: typeof height === 'string' ? height : `${height}px` }}
      >
        {Object.entries(groupedItems).map(([groupName, groupItems]) => (
          <div key={groupName} className="mb-6 last:mb-0">
            {/* Group header */}
            {groupBy && groupName !== 'all' && (
              <h4 className="text-lg font-semibold text-gray-800 mb-4 sticky top-0 bg-inherit z-10 py-2">
                {groupName}
              </h4>
            )}
            
            {/* Group items */}
            <div className={cn(
              isGridView ? 'grid gap-4' : 'space-y-2',
              isGridView && 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            )}>
              {groupItems.map((item, index) => renderListItem(item, index))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

BirhausListV3.displayName = 'BirhausListV3'

/**
 * Hook for managing list state with v3 enhancements
 */
export const useBirhausListV3 = <T extends BirhausListV3Item>(initialItems: T[] = []) => {
  const [items, setItems] = React.useState<T[]>(initialItems)
  const [selectedItems, setSelectedItems] = React.useState<Set<string>>(new Set())
  const [searchTerm, setSearchTerm] = React.useState('')
  const [filters, setFilters] = React.useState<Record<string, any>>({})
  const [loading, setLoading] = React.useState(false)
  
  const handleSelect = (item: T) => {
    setSelectedItems(new Set([item.id]))
  }
  
  const handleMultiSelect = (itemIds: Set<string>) => {
    setSelectedItems(itemIds)
  }
  
  const handleFilter = (newFilters: Record<string, any>) => {
    setFilters(newFilters)
  }
  
  const clearSelection = () => {
    setSelectedItems(new Set())
  }
  
  const selectAll = (allItems: T[]) => {
    setSelectedItems(new Set(allItems.map(item => item.id)))
  }
  
  return {
    items,
    setItems,
    selectedItems,
    searchTerm,
    setSearchTerm,
    filters,
    loading,
    setLoading,
    handleSelect,
    handleMultiSelect,
    handleFilter,
    clearSelection,
    selectAll,
  }
}